// src/components/wizards/FinalizeStep.tsx
import React, { useState } from 'react'
import { Row, Col, Button, Alert, Card } from 'react-bootstrap'
import { useProject } from './NewProjectContext'
import FileUploadStats from '../FileUploadStats'
import {
  useUploadTiffMutation,
  useGetProjectQuery,
  useUploadInitMutation,
  useUploadProjectContentMutation,
  useVectorizeProjectMutation,
} from '../../services/projectsApi'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectSelectedProjectId } from '../../store/slices/projectSlice'
import {
  resetAllStats,
  selectDatasetSources,
  setDatasetSources,
  setDatasetStats,
  setFilesUploading,
  setProgress,
  setError,
  selectFilesUploading,
  selectProgress,
  selectError,
} from '../../store/slices/datasetSlice'
import { annotationsApi } from '../../services/annotationApi'
import { apiTags } from '../../services/apiTags'
import { tagApi } from '../../services/tagApi'
import { contentApi } from '../../services/contentApi'
import { datasetApi } from '../../services/datasetApi'
import styled from 'styled-components'
import { FileText, CloudUpload, CheckCircle } from 'lucide-react'
import ErrorDialog from '../modals/ErrorDialog'
import { UploadProjectResp, UploadProjectContentRequest } from '../../types/api/api-project'
import { UploadErr } from '../../types/common'

const StyledCard = styled(Card)`
  background: #212529;
  border: none;
  border-radius: 0.5rem;
  overflow: hidden;
`

const ProgressContainer = styled.div`
  background: rgba(64, 37, 99, 0.1);
  border-radius: 0.5rem;
  padding: 1.25rem;
  text-align: center;
  border: 1px solid rgba(64, 37, 99, 0.2);
  margin-top: 1.5rem;
`

const ProgressBar = styled.div<{ $progress: number }>`
  background: rgba(64, 37, 99, 0.3);
  height: 6px;
  border-radius: 3px;
  position: relative;
  margin-top: 0.75rem;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: ${(props) => props.$progress}%;
    height: 100%;
    background: linear-gradient(90deg, #402563, #114ee0);
    transition: width 0.6s ease;
  }
`

const ProgressPercentage = styled.div`
  color: #114ee0;
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1;
`

const ReviewSection = styled.div`
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    border-bottom: none;
  }

  h6 {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.875rem;
    font-weight: 600;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`

const ReviewItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  color: #ffffff;

  .label {
    width: 140px;
    color: rgba(255, 255, 255, 0.7);
    font-weight: 500;
  }

  .value {
    flex: 1;
    font-weight: 500;
  }
`

interface UploadButtonProps {
  onClick: () => void
  disabled: boolean
  children: React.ReactNode
}

const UploadButton: React.FC<UploadButtonProps> = ({ onClick, disabled, children }) => {
  const buttonStyle = {
    background: disabled ? '#343a40' : 'linear-gradient(45deg, #402563, #114ee0)',
    border: 'none',
    padding: '0.75rem 2rem',
    fontWeight: 500,
    transition: 'all 0.2s ease',
    width: '100%',
    marginTop: '1.5rem',
    opacity: disabled ? 0.7 : 1,
    transform: 'none',
    color: '#ffffff',
  }

  return (
    <Button onClick={onClick} disabled={disabled} style={buttonStyle} className="upload-button">
      {children}
      <style>
        {`
          .upload-button:not(:disabled):hover {
            background: linear-gradient(45deg, #4e2d7c, #1757e8) !important;
            transform: translateY(-1px);
            color: #ffffff !important;
          }
          .upload-button:disabled {
            color: #ffffff !important;
          }
        `}
      </style>
    </Button>
  )
}

const ReviewCard: React.FC<{
  datasetName: string
  numFiles: number
  hasLabels: boolean
  labelsFileName?: string
  labelsFormat?: string
}> = ({ datasetName, numFiles, hasLabels, labelsFileName, labelsFormat }) => (
  <StyledCard>
    <Card.Body className="p-4">
      <h5 className="text-light mb-4">Review Your Selections</h5>

      <ReviewSection>
        <h6>
          <CheckCircle size={16} /> Dataset Details
        </h6>
        <ReviewItem>
          <span className="label">Dataset Name:</span>
          <span className="value">{datasetName}</span>
        </ReviewItem>
      </ReviewSection>

      <ReviewSection>
        <h6>
          <FileText size={16} /> Upload Details
        </h6>
        <ReviewItem>
          <span className="label">Images Selected:</span>
          <span className="value">
            {numFiles} file{numFiles !== 1 ? 's' : ''}
          </span>
        </ReviewItem>
        <ReviewItem>
          <span className="label">Labels:</span>
          <span className="value">{hasLabels ? 'Yes' : 'No'}</span>
        </ReviewItem>
        {hasLabels && labelsFileName && (
          <>
            <ReviewItem>
              <span className="label">Labels File:</span>
              <span className="value">{labelsFileName}</span>
            </ReviewItem>
            <ReviewItem>
              <span className="label">Format:</span>
              <span className="value">{labelsFormat?.toUpperCase()}</span>
            </ReviewItem>
          </>
        )}
      </ReviewSection>
    </Card.Body>
  </StyledCard>
)

interface UploadFinalizeStepProps {
  onHide: () => void
}

const CHUNK_SIZE = 20 * 1024 * 1024 // 5MB chunks
const MAX_CONCURRENT_BATCHES = 5 // Number of concurrent batches
const MAX_BATCH_SIZE = 20 // Maximum number of files per batch

interface FileUploadProgress {
  fileName: string
  progress: number
  isComplete: boolean
}

class UploadPool {
  private queue: File[][] = [] // Queue of file batches
  private active = 0
  private completed = 0
  private totalBatches = 0

  constructor(
    private uploadId: string,
    private metadata: any,
    private datasetId: string,
    private uploadProjectContent: (params: UploadProjectContentRequest) => {
      unwrap: () => Promise<UploadProjectResp>
    },
    private onProgress: (progress: number) => void,
    private onFileProgress?: (progress: FileUploadProgress) => void,
    private onFileComplete?: (stats: UploadProjectResp) => void
  ) {}

  private async processNext() {
    if (this.active >= MAX_CONCURRENT_BATCHES || this.queue.length === 0) return

    this.active++
    const batch = this.queue.shift()
    if (!batch) {
      this.active--
      return
    }

    try {
      // Mark all files in batch as starting upload
      batch.forEach((file) => {
        this.onFileProgress?.({
          fileName: file.name,
          progress: 0,
          isComplete: false,
        })
      })

      const result = await this.uploadProjectContent({
        upload_id: this.uploadId,
        datasetid: this.datasetId,
        files: batch,
        metadata: this.metadata,
      }).unwrap()

      // Mark all files in batch as complete
      batch.forEach((file) => {
        this.onFileProgress?.({
          fileName: file.name,
          progress: 100,
          isComplete: true,
        })
      })

      this.completed++
      this.onProgress((this.completed / this.totalBatches) * 100)

      // Update stats for the entire batch
      this.onFileComplete?.(result)
    } catch (error: any) {
      console.error('Failed to upload batch:', error)
      const uploadError: UploadErr = {
        error: error.message,
        internalError: error.message,
        filename: batch.map((f) => f.name).join(', '),
        message: `Failed to upload batch: ${error.message}`,
      }

      const failedStats: UploadProjectResp = {
        errors: [uploadError],
        total_bytes: batch.reduce((sum, file) => sum + file.size, 0),
        total_files: batch.length,
        total_files_failed: batch.length,
        total_files_succeeded: 0,
        total_files_duplicate: 0,
        labels_file: '',
      }

      this.completed++
      this.onProgress((this.completed / this.totalBatches) * 100)

      this.onFileComplete?.(failedStats)
    } finally {
      this.active--
      await this.processNext()
    }
  }

  async uploadAll(files: File[]): Promise<void> {
    // Create batches based on file sizes
    let currentBatch: File[] = []
    let currentBatchSize = 0

    for (const file of files) {
      // If file is larger than chunk size, create a single-file batch
      if (file.size > CHUNK_SIZE) {
        if (currentBatch.length > 0) {
          this.queue.push([...currentBatch])
          currentBatch = []
          currentBatchSize = 0
        }
        this.queue.push([file])
        continue
      }

      // If adding this file would exceed chunk size or max batch size, create a new batch
      if (currentBatchSize + file.size > CHUNK_SIZE || currentBatch.length >= MAX_BATCH_SIZE) {
        if (currentBatch.length > 0) {
          this.queue.push([...currentBatch])
          currentBatch = []
          currentBatchSize = 0
        }
      }

      currentBatch.push(file)
      currentBatchSize += file.size
    }

    // Push any remaining files
    if (currentBatch.length > 0) {
      this.queue.push(currentBatch)
    }

    this.totalBatches = this.queue.length

    // Start initial batch processes
    const initialProcesses = Math.min(MAX_CONCURRENT_BATCHES, this.totalBatches)
    await Promise.all(
      Array(initialProcesses)
        .fill(null)
        .map(() => this.processNext())
    )

    // Wait for all uploads to complete
    while (this.completed < this.totalBatches) {
      await new Promise((resolve) => setTimeout(resolve, 100))
    }
  }
}

const uploadFilesWithProgress = async (
  uploadId: string,
  files: File[],
  metadata: any,
  datasetId: string,
  uploadProjectContent: any,
  onProgress: (progress: number) => void,
  onFileProgress?: (fileProgress: FileUploadProgress) => void,
  onFileComplete?: (stats: UploadProjectResp) => void
): Promise<void> => {
  const pool = new UploadPool(
    uploadId,
    metadata,
    datasetId,
    uploadProjectContent,
    onProgress,
    onFileProgress,
    onFileComplete
  )

  await pool.uploadAll(files)
}

const UploadFinalizeStep: React.FC<UploadFinalizeStepProps> = ({ onHide }) => {
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const selectedDataset = useAppSelector(selectDatasetSources)
  const [showErrorDialog, setShowErrorDialog] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const {
    fileMap,
    tiffFileMap,
    selectedFiles,
    selectedTiffFiles,
    labelsFile,
    labelsFormat,
    datasetName,
    clearFiles,
    datasetSources,
  } = useProject()

  const [uploadTiff] = useUploadTiffMutation()
  const [uploadInit] = useUploadInitMutation()
  const [uploadProjectContent] = useUploadProjectContentMutation()
  const [vectorizeProject] = useVectorizeProjectMutation()

  const uploading = useAppSelector(selectFilesUploading)
  const progress = useAppSelector(selectProgress)
  const error = useAppSelector(selectError)

  const calculateOverallProgress = (
    tiffProgress: number,
    regularProgress: number,
    totalTiffFiles: number,
    totalRegularFiles: number
  ) => {
    if (totalTiffFiles + totalRegularFiles === 0) return 0
    const tiffWeight = totalTiffFiles / (totalTiffFiles + totalRegularFiles)
    const regularWeight = totalRegularFiles / (totalTiffFiles + totalRegularFiles)
    return Math.round(tiffProgress * tiffWeight + regularProgress * regularWeight)
  }

  const handleUpload = async () => {
    console.log('Upload started with:', {
      selectedProjectId,
      datasetName,
      selectedDataset,
      fileMapSize: fileMap.size,
      tiffFileMapSize: tiffFileMap.size,
      labelsFile: labelsFile?.name,
    })

    dispatch(resetAllStats())
    const uploadProjectId = selectedProjectId // Store the selected project ID

    if (uploadProjectId && (datasetName || selectedDataset?.[0])) {
      const dsName = datasetName ? datasetName : selectedDataset[0]
      console.log('Starting upload for dataset:', dsName)

      const setUploadingAction = setFilesUploading(true)
      const setProgressAction = setProgress(0)

      dispatch(setUploadingAction)
      dispatch(setProgressAction)

      const metadata = { datasource: dsName }

      try {
        // First, initialize the upload to get the uploadId
        const initResponse = await uploadInit({
          projectId: uploadProjectId,
          name: dsName,
          ...(labelsFile && {
            labels_file: labelsFile.name,
            labels_format: labelsFormat,
            labels: labelsFile,
          }),
        }).unwrap()

        const tiffFilesToUpload = selectedTiffFiles
          .map((meta) => tiffFileMap.get(meta.id))
          .filter(Boolean) as File[]
        const regularFilesToUpload = selectedFiles
          .map((meta) => fileMap.get(meta.id))
          .filter(Boolean) as File[]
        let tiffProgress = 0
        let regularProgress = 0

        let totalStats: UploadProjectResp = {
          errors: [],
          labels_file: '',
          total_bytes: 0,
          total_files: 0,
          total_files_duplicate: 0,
          total_files_failed: 0,
          total_files_succeeded: 0,
        }

        // Process TIFF files in batches
        if (tiffFilesToUpload.length > 0) {
          const batches: File[][] = []
          let currentBatch: File[] = []
          let currentBatchSize = 0

          for (const file of tiffFilesToUpload) {
            if (file.size > CHUNK_SIZE) {
              if (currentBatch.length > 0) {
                batches.push([...currentBatch])
                currentBatch = []
                currentBatchSize = 0
              }
              batches.push([file])
              continue
            }

            if (
              currentBatch.length >= MAX_BATCH_SIZE ||
              currentBatchSize + file.size > CHUNK_SIZE
            ) {
              batches.push([...currentBatch])
              currentBatch = []
              currentBatchSize = 0
            }

            currentBatch.push(file)
            currentBatchSize += file.size
          }

          if (currentBatch.length > 0) {
            batches.push(currentBatch)
          }

          // Process TIFF batches with MAX_CONCURRENT_BATCHES at a time
          for (let i = 0; i < batches.length; i += MAX_CONCURRENT_BATCHES) {
            const batchGroup = batches.slice(i, i + MAX_CONCURRENT_BATCHES)
            const results = await Promise.all(
              batchGroup.map((batchFiles) =>
                uploadTiff({
                  id: uploadProjectId,
                  datasetid: project?.datasetid || '',
                  files: batchFiles,
                  clip_size: 1024,
                  metadata,
                }).unwrap()
              )
            )

            // Aggregate TIFF results
            totalStats = results.reduce(
              (
                acc: {
                  errors: any
                  labels_file: any
                  total_bytes: any
                  total_files: any
                  total_files_duplicate: any
                  total_files_failed: any
                  total_files_succeeded: any
                },
                result: {
                  errors: any
                  labels_file: any
                  total_bytes: any
                  total_files: any
                  total_files_duplicate: any
                  total_files_failed: any
                  total_files_succeeded: any
                }
              ) => ({
                errors: [...acc.errors, ...result.errors],
                labels_file: result.labels_file || acc.labels_file,
                total_bytes: acc.total_bytes + result.total_bytes,
                total_files: acc.total_files + result.total_files,
                total_files_duplicate: acc.total_files_duplicate + result.total_files_duplicate,
                total_files_failed: acc.total_files_failed + result.total_files_failed,
                total_files_succeeded: acc.total_files_succeeded + result.total_files_succeeded,
              }),
              totalStats
            )

            // Update progress
            tiffProgress = ((i + batchGroup.length) / batches.length) * 100
            const overallProgress = calculateOverallProgress(
              tiffProgress,
              regularProgress,
              tiffFilesToUpload.length,
              regularFilesToUpload.length
            )
            dispatch(setProgress(overallProgress))

            // Update stats in Redux
            dispatch(setDatasetStats(totalStats))
          }
        }

        // Process regular files with HTTP batching
        if (regularFilesToUpload.length > 0) {
          await uploadFilesWithProgress(
            initResponse.upload_id,
            regularFilesToUpload,
            metadata,
            project?.datasetid || '',
            uploadProjectContent,
            (progress) => {
              regularProgress = progress
              const overallProgress = calculateOverallProgress(
                tiffProgress,
                regularProgress,
                tiffFilesToUpload.length,
                regularFilesToUpload.length
              )
              dispatch(setProgress(overallProgress))
            },
            (fileProgress) => {
              // Optional: Handle individual file progress if needed
            },
            (stats) => {
              // Update total stats
              totalStats = {
                errors: [...totalStats.errors, ...stats.errors],
                labels_file: stats.labels_file || totalStats.labels_file,
                total_bytes: totalStats.total_bytes + stats.total_bytes,
                total_files: totalStats.total_files + stats.total_files,
                total_files_duplicate:
                  totalStats.total_files_duplicate + stats.total_files_duplicate,
                total_files_failed: totalStats.total_files_failed + stats.total_files_failed,
                total_files_succeeded:
                  totalStats.total_files_succeeded + stats.total_files_succeeded,
              }

              // Update Redux with combined stats
              dispatch(setDatasetStats(totalStats))

              // Show error dialog immediately if this file failed
              if (stats.total_files_failed > 0) {
                const errorMessage = stats.errors
                  .map((err: UploadErr) => `${err.filename}: ${err.error}`)
                  .join('\n')
                dispatch(setError('Upload failed'))
                setErrorMessage(errorMessage)
                setShowErrorDialog(true)
              }
            }
          )
        }

        // Invalidate caches after successful upload
        dispatch(
          annotationsApi.util.invalidateTags([
            { type: apiTags.AnnotationStats, id: project?.datasetid || '' },
            { type: apiTags.AnnotationStats, id: 'LIST' },
            { type: apiTags.Annotation, id: 'LIST' },
          ])
        )

        dispatch(
          contentApi.util.invalidateTags([
            { type: apiTags.Content, id: 'LIST' },
            { type: apiTags.Metadata, id: uploadProjectId },
            { type: apiTags.Metadata, id: 'LIST' },
          ])
        )

        dispatch(
          datasetApi.util.invalidateTags([
            { type: apiTags.Dataset, id: project?.datasetid || '' },
            { type: apiTags.DatasetList, id: 'LIST' },
          ])
        )

        dispatch(
          tagApi.util.invalidateTags([
            { type: apiTags.Tag, id: 'LIST' },
            { type: apiTags.TagProperties, id: project?.datasetid || '' },
          ])
        )

        // Trigger vectorization in the background after successful upload
        if (totalStats.total_files_succeeded > 0) {
          console.log(
            'Upload completed successfully, starting vectorization in background for project:',
            uploadProjectId
          )
          // Fire and forget - don't await this call so upload flow continues normally
          vectorizeProject({ projectId: uploadProjectId })
            .unwrap()
            .then(() => {
              console.log('Vectorization initiated successfully')
            })
            .catch((vectorizeError: any) => {
              console.error('Failed to start vectorization:', vectorizeError)
              // Vectorization failure doesn't affect the upload success
            })
        }

        onHide()
        clearFiles()
        dispatch(setDatasetSources([]))
      } catch (err: any) {
        dispatch(setError('An error occurred during upload. Please try again.'))
        setErrorMessage(err?.data?.message || 'An error occurred during upload. Please try again.')
        setShowErrorDialog(true)
      } finally {
        dispatch(setFilesUploading(false))
      }
    }
  }

  return (
    <div className="p-4">
      <Row className="justify-content-center">
        <Col md={8}>
          {!uploading ? (
            <>
              <ReviewCard
                datasetName={datasetName || selectedDataset?.[0] || ''}
                numFiles={fileMap.size + tiffFileMap.size}
                hasLabels={!!labelsFile}
                labelsFileName={labelsFile?.name}
                labelsFormat={labelsFormat}
              />

              {error && (
                <Alert variant="danger" className="mt-4 mb-0">
                  {error}
                </Alert>
              )}

              <UploadButton
                onClick={handleUpload}
                disabled={
                  fileMap.size + tiffFileMap.size === 0 ||
                  uploading ||
                  (!datasetName && datasetSources.length === 0)
                }
              >
                <CloudUpload size={16} className="me-2" />
                Start Upload
              </UploadButton>
            </>
          ) : (
            <StyledCard>
              <Card.Body className="p-4">
                <h5 className="text-light mb-3">Upload Progress</h5>
                <FileUploadStats
                  numFilesSelected={fileMap.size + tiffFileMap.size}
                  labelsFileName={labelsFile?.name || 'None'}
                  datasetSource={datasetName || ''}
                />

                <ProgressContainer>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <ProgressPercentage>{Math.round(progress)}%</ProgressPercentage>
                  </div>
                  <ProgressBar $progress={Math.round(progress)} />
                </ProgressContainer>
              </Card.Body>
            </StyledCard>
          )}
        </Col>
      </Row>

      <ErrorDialog
        show={showErrorDialog}
        onHide={() => setShowErrorDialog(false)}
        message={errorMessage}
        title="Upload Error"
      />
    </div>
  )
}

export default UploadFinalizeStep
